import React, { useEffect, useState } from "react";
import api from "@/lib/api";
import { useDashboardCtx } from "@/context/DashboardContext";
import { toast } from "sonner";
import { Loader2, ChevronRight } from "lucide-react";

const STAGES = [
  { id: "NEW", label: "New Leads" },
  { id: "CONTACTED", label: "Contacted (WA)" },
  { id: "QUALIFIED", label: "Qualified" },
  { id: "PROPOSAL", label: "Proposal / Demo" },
  { id: "FOLLOWUP", label: "Follow-up" },
  { id: "SALE", label: "Closed Sale" },
  { id: "LOST", label: "Lost" },
];
const SCORE = { HOT: "border-l-destructive", WARM: "border-l-primary", COLD: "border-l-muted-foreground" };

export default function CrmPipeline() {
  const { customerId, readOnly } = useDashboardCtx();
  const [leads, setLeads] = useState(null);

  useEffect(() => {
    api.get("/leads", { params: customerId ? { customer_id: customerId } : {} })
      .then((r) => setLeads(r.data)).catch(() => {});
  }, [customerId]);

  const move = async (lead, dir) => {
    const idx = STAGES.findIndex((s) => s.id === lead.stage);
    const next = STAGES[idx + dir];
    if (!next) return;
    setLeads((ls) => ls.map((l) => (l.id === lead.id ? { ...l, stage: next.id } : l)));
    try { await api.patch(`/leads/${lead.id}`, { stage: next.id }); toast.success(`${lead.name} → ${next.label}`); }
    catch (e) { toast.error("Update failed"); }
  };

  if (!leads) return <div className="flex justify-center py-20"><Loader2 className="w-6 h-6 animate-spin text-primary" /></div>;

  return (
    <div className="animate-float-up" data-testid="crm-panel">
      <div className="flex gap-4 overflow-x-auto scrollbar-thin pb-4">
        {STAGES.map((s) => {
          const items = leads.filter((l) => l.stage === s.id);
          return (
            <div key={s.id} className="w-64 shrink-0" data-testid={`crm-column-${s.id}`}>
              <div className="flex items-center justify-between mb-3 px-1">
                <span className="text-sm font-semibold">{s.label}</span>
                <span className="text-xs font-mono px-2 py-0.5 rounded-full bg-secondary text-muted-foreground">{items.length}</span>
              </div>
              <div className="space-y-2 min-h-[100px]">
                {items.map((l) => (
                  <div key={l.id} data-testid={`crm-card-${l.id}`} className={`bg-card border border-border border-l-2 ${SCORE[l.score]} rounded-xl p-3`}>
                    <p className="text-sm font-medium">{l.name}</p>
                    <p className="text-xs text-muted-foreground">{l.product}</p>
                    <div className="flex items-center justify-between mt-2">
                      <span className="text-[10px] font-mono px-2 py-0.5 rounded-full bg-secondary text-muted-foreground">{l.score}</span>
                      {!readOnly && s.id !== "SALE" && s.id !== "LOST" && (
                        <button data-testid={`crm-advance-${l.id}`} onClick={() => move(l, 1)} className="text-xs text-primary hover:text-primary/80 flex items-center gap-0.5 transition-colors">Move <ChevronRight className="w-3 h-3" /></button>
                      )}
                    </div>
                  </div>
                ))}
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
}
